import React, {useState, useEffect, useMemo, useRef} from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './styles.module.css'
import { Spinner } from 'react-bootstrap';
import { getCurrentYear } from '../../utils/getItemFromRange';
import { MdRefresh } from 'react-icons/md';
import { FaRegTrashAlt } from 'react-icons/fa';
import { usePermission } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import { cathList2 } from '../../actions/cathedra';
import * as disciplineActions from '../../actions/discipline'
import { schoolYear } from '../../actions/radar';
import { getLessonPlanByYearAndFuk } from '../../actions/lessonPlan';
import { getDisciplinesForComplexTopicsDisciplines } from '../../actions/complexTopicsDisciplines';
import { itemsPerPage } from '../../utils/const';
import usePageWidth from '../shared/usePageWidth';
import CustomSelect from '../CustomSelect';

// «Все» в CustomSelect — пустая строка, бэк ждёт 0 как «без фильтра» — см. toParam
const ALL = '';

const initialFilters = {
    year: null,
    cafedra: ALL,
    plan: ALL,
    discipline: ALL,
    numberPage: 0
};

// перечни приходят разными методами — идентификатор и наименование у каждого свои
const optionsOf = (list, valueKey, labelKeys) => {
    const seen = new Set();
    return (list || [])
        .map(item => ({
            value: item[valueKey],
            name: labelKeys.map(key => item[key]).find(Boolean) || ''
        }))
        .filter(({ value }) => {
            if (value == null || seen.has(value)) return false;
            seen.add(value);
            return true;
        });
};

const byName = (a, b) => a.name.localeCompare(b.name, 'ru');

// в запрос уходит 0, а не пустая строка
const toParam = value => (value === ALL || value == null ? 0 : value);

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
    if (pages <= 1) return [];
    const keep = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
    if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
    const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

    const items = [];
    list.forEach((value, i) => {
        if (i) {
            const gap = value - list[i - 1];
            if (gap === 2) items.push(list[i - 1] + 1);
            else if (gap > 2) items.push('gap');
        }
        items.push(value);
    });
    return items;
};

const Flow_plan = ({
    getYears,
    getCafedra,
    getPlans,
    getDisciplines,
    years,
    cafedras,
    plansByCathedra,
    disciplines,
    getComplexThemePlanStream,
    complexData,
    isLoadingComplex,
    getLessonPlanByYearAndFuk,
    lessonPlanListCur,
    pageCount,
    totalCount,
    streamsErrorStatus,
    clearComplexData
    }) => {

    usePageWidth('extraWide');

    const { readRight } = usePermission('Справочники', 'Комплексный тем.план (КТП) потоков');

    const history = useHistory();
    const location = useLocation();

    // возврат из карточки по «← Назад» — условия отбора и страница списка сохраняются
    const restored = useRef(location.state?.flowPlanList || null);

    const [state, setState] = useState(restored.current?.filters || initialFilters);
    const [defaultYear, setDefaultYear] = useState(restored.current?.defaultYear ?? null);
    const [loaded, setLoaded] = useState(!!restored.current?.loaded);

    const yearOptions = useMemo(
        () => optionsOf(years, 'xp_key', ['name'])
            .sort((a, b) => b.name.localeCompare(a.name, 'ru')),
        [years]
    );

    const cafedraOptions = useMemo(
        () => optionsOf(cafedras, 'idcathedra', ['cathedra', 'name']).sort(byName),
        [cafedras]
    );

    // при выбранной кафедре планы берутся по кафедре, при не выбранной — по учебному году
    const planOptions = useMemo(
        () => optionsOf(
            state.cafedra ? plansByCathedra : (lessonPlanListCur?.length ? lessonPlanListCur : plansByCathedra),
            'idcurriculum',
            ['cname', 'name', 'title']
        ).sort(byName),
        [state.cafedra, plansByCathedra, lessonPlanListCur]
    );

    const disciplineOptions = useMemo(
        () => optionsOf(disciplines, 'cid', ['title', 'predmet', 'name']).sort(byName),
        [disciplines]
    );

    useEffect(() => {
        getYears();
        getCafedra();
        return () => clearComplexData();
    }, [])

    useEffect(() => {
        if (!years?.length) return;
        const currentYear = getCurrentYear(years)?.xp_key ?? null;
        setDefaultYear(currentYear);
        setState(prev => prev.year === null ? {...prev, year: currentYear} : prev);
    }, [years])

    useEffect(() => {
        state.year && getLessonPlanByYearAndFuk(state.year, toParam(state.cafedra));
    }, [state.year])

    useEffect(() => {
        getPlans(toParam(state.cafedra));
    }, [state.cafedra]);

    useEffect(() => {
        getDisciplines(toParam(state.plan));
    }, [state.plan]);

    const fetchData = (page = 0, filters = state) => {
        if (filters.year !== null) {
            getComplexThemePlanStream(
                toParam(filters.year),
                toParam(filters.cafedra),
                toParam(filters.plan),
                toParam(filters.discipline),
                page
            )
        }
    }

    // возврат из карточки: список перезапрашивается по сохранённым условиям и странице
    useEffect(() => {
        const snapshot = restored.current;
        if (snapshot?.loaded) fetchData(snapshot.filters.numberPage, snapshot.filters);
    }, []);

    // смена значения сбрасывает нижестоящие поля в «Все»
    const handleChange = (name, value) => {
        setState(prev => {
            if (name === 'year') return {...prev, year: value, cafedra: ALL, plan: ALL, discipline: ALL};
            if (name === 'cafedra') return {...prev, cafedra: value, plan: ALL, discipline: ALL};
            if (name === 'plan') return {...prev, plan: value, discipline: ALL};
            return {...prev, [name]: value};
        });
    }

    const handleRefresh = () => {
        setState(prev => ({...prev, numberPage: 0}))
        setLoaded(true)
        fetchData(0)
    }

    const handlePageChange = (page) => {
        setState(prev => ({...prev, numberPage: page}))
        fetchData(page)
        window.scrollTo({top: 0, behavior: 'smooth'})
    }

    const isFiltersDirty = state.year !== defaultYear
        || state.cafedra !== ALL
        || state.plan !== ALL
        || state.discipline !== ALL;

    const handleResetFilters = () => {
        setState(prev => ({...prev, year: defaultYear, cafedra: ALL, plan: ALL, discipline: ALL}));
    }

    const handleRowClick = (id_ctps) => {
        history.push({
            pathname: `/flow_plan/${id_ctps}`,
            state: { flowPlanList: { filters: state, defaultYear, loaded } }
        });
    }

    // ширина — на обёртке поля, само поле тянется на 100%; подпись рисует CustomSelect (title)
    const renderField = (label, name, options, widthClass) => (
        <div className={`${styles.filterItem} ${widthClass}`}>
            <CustomSelect
                title={label}
                width="100%"
                options={options}
                value={state[name] ?? ALL}
                setFieldValue={handleChange}
                valuesName={name}
            />
        </div>
    );

    const renderValue = (value) => (value || value === 0)
        ? value
        : <span className={styles.dash}>—</span>;

    const fromRecord = totalCount ? state.numberPage * itemsPerPage + 1 : 0;
    const toRecord = Math.min((state.numberPage + 1) * itemsPerPage, totalCount);
    const rows = complexData || [];
    const currentPage = state.numberPage + 1;

    if (!readRight) return <HaventPermission />;

    return (
    <div className={styles.page}>
        <div className={styles.head}>
            <h1 className={styles.title}>Комплексный тематический план (КТП) потоков</h1>
            <div className={styles.headRight}>
                <button
                    className={styles.resetBtn}
                    onClick={handleResetFilters}
                    disabled={!isFiltersDirty}
                >
                    Сбросить фильтры
                    <FaRegTrashAlt size={16} />
                </button>
            </div>
        </div>

        <div className={styles.filters}>
            {renderField('Учебный год', 'year', yearOptions, styles.fYear)}
            {renderField('Кафедра', 'cafedra', cafedraOptions, styles.fKaf)}
            {renderField('Учебный план', 'plan', planOptions, styles.fPlan)}
            {renderField('Дисциплина', 'discipline', disciplineOptions, styles.fDisc)}
            <button className={styles.refreshBtn} onClick={handleRefresh}>
                <MdRefresh size={17} />
                Обновить
            </button>
        </div>

        {isLoadingComplex ? (
            <div className={styles.loader}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>
        ) : streamsErrorStatus ? (
            <div className={styles.error}>
                <div className={styles.errorTitle}>Не удалось загрузить перечень</div>
                {streamsErrorStatus === 403
                    ? 'Доступ к режиму запрещён'
                    : 'Сервер вернул ошибку'}
                <div>Повторите попытку по кнопке «Обновить».</div>
            </div>
        ) : (!loaded || rows.length === 0 ? (
            <div className={styles.empty}>
                <div className={styles.emptyTitle}>По заданным параметрам данные отсутствуют</div>
                {!loaded && 'Задайте условия отбора и нажмите «Обновить»'}
            </div>
        ) : (
            <>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Поток</th>
                                <th>Учебный план</th>
                                <th>Дисциплина</th>
                                <th className={styles.colSt}>Состав</th>
                                <th className={styles.colSt}>Курс</th>
                                <th className={styles.colSt}>Семестр</th>
                                <th className={styles.colHh}>Часов</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((el, ind) =>
                                <tr key={el.id_ctps ?? ind} onClick={() => handleRowClick(el.id_ctps)}>
                                    <td className={styles.strong}>{renderValue(el.stream)}</td>
                                    <td className={styles.left}>{renderValue(el.study_plan)}</td>
                                    <td className={styles.left}>{renderValue(el.discipline)}</td>
                                    <td className={`${styles.colSt} ${styles.mono}`}>{renderValue(el.membership)}</td>
                                    <td className={styles.colSt}>{renderValue(el.course)}</td>
                                    <td className={styles.colSt}>{renderValue(el.term)}</td>
                                    <td className={styles.colHh}>{renderValue(el.hours)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles.footrow}>
                    <span className={styles.total}>
                        Показано <b>{fromRecord}-{toRecord}</b> из <b>{totalCount}</b> записей
                    </span>
                    <div className={styles.pager}>
                        {pagerItems(currentPage, pageCount).map((item, ind) => item === 'gap'
                            ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                            : <button
                                key={item}
                                className={item === currentPage ? styles.pageOn : undefined}
                                onClick={() => handlePageChange(item - 1)}
                              >
                                {item}
                              </button>
                        )}
                    </div>
                </div>
            </>
        ))}
    </div>
  )
}

const mapStateToProps = ({schedule, cathedra, umk, discipline, radar, lessonPlan, complexTopicsDisciplines}) => ({
    years: radar.scy,
    cafedras: cathedra.list2,
    plansByCathedra: discipline.plansByCathedra,
    disciplines: complexTopicsDisciplines.disciplines,
    pageCount:  Math.ceil((complexTopicsDisciplines.data.streams.count || 0) / itemsPerPage),
    totalCount:  complexTopicsDisciplines.data.streams.count || 0,
    complexData: complexTopicsDisciplines.data.streams.complex_theme_plan_stream,
    streamsErrorStatus: complexTopicsDisciplines.data.streamsErrorStatus,
    isLoadingComplex: complexTopicsDisciplines.isLoading,
    lessonPlanListCur: lessonPlan.lessonPlanListCur,
});

const mapDispatchToProps = dispatch => ({
    getYears: ()=> dispatch(schoolYear()),
    getCafedra: (id) => dispatch(cathList2(id)),
    getPlans: (carhedraId) => dispatch(disciplineActions.fetchCirriculumByCafedra(carhedraId)),
    getDisciplines: termId => dispatch(getDisciplinesForComplexTopicsDisciplines(termId)),
    getLessonPlanByYearAndFuk: (year, faculty) => dispatch(getLessonPlanByYearAndFuk(year, faculty)),
    getComplexThemePlanStream: (year, cafedra, plan, discipline, page) => dispatch(disciplineActions.getComplexThemePlanStream(year, cafedra, plan, discipline, page)),
    clearComplexData: () => dispatch(disciplineActions.clearComplexThemePlanStream())
});

export default connect(mapStateToProps, mapDispatchToProps)(Flow_plan);
